import { Component } from "projen";
import { Jest, NodeProject } from "projen/lib/javascript";
import { TextFile } from "projen/lib/textfile";
import { PnpmWorkspace } from "../pnpm";
import { VERSION } from "../versions";

/**
 * Options for the Vitest config (test block in vitest.config).
 *
 * @see https://vitest.dev/config/
 */
export interface VitestConfigOptions {
  /**
   * Glob patterns for test files.
   *
   * @default `["**\/*.{test,spec}.?(c|m)[jt]s?(x)"]`
   */
  readonly include?: string[];

  /**
   * Glob patterns to exclude from test files.
   *
   * @default ["**\/node_modules\/**", "**\/dist\/**", "**\/lib\/**", "**\/.?\*"]
   */
  readonly exclude?: string[];

  /**
   * Test environment.
   *
   * @default "node"
   */
  readonly environment?: "node" | "jsdom" | "happy-dom" | "edge-runtime";

  /**
   * Do not fail when no tests are found.
   *
   * @default true
   */
  readonly passWithNoTests?: boolean;

  /**
   * Enable coverage collection.
   *
   * @default true
   */
  readonly coverageEnabled?: boolean;

  /**
   * Coverage reports directory.
   *
   * @default "coverage"
   */
  readonly coverageDirectory?: string;

  /**
   * Glob patterns for the files coverage is measured against.
   *
   * Rendered as `coverage.include`, which Vitest 4 requires: it ships no
   * default, and coverage with none set silently measures nothing — the
   * report comes back empty and summarises as 100%. Vitest 3's `coverage.all`
   * (removed in 4) is what used to cover this implicitly.
   *
   * Scoping this to the source root also keeps build output (`dist/`, `lib/`)
   * out of the report, which Vitest 3 got from a default `coverage.exclude`
   * list that version 4 reduces to `[]`.
   *
   * The default carries an explicit script-extension filter because Vitest 4
   * also removed `coverage.extensions`: a bare `src/**` sweeps every matched
   * file into AST remapping, and each non-script file (markdown, astro, ...)
   * logs a PARSE_ERROR during the uncovered-files pass.
   *
   * @default ["src/**\/*.{js,cjs,mjs,jsx,ts,cts,mts,tsx}"]
   */
  readonly coverageInclude?: string[];

  /**
   * Coverage reporters.
   *
   * @default ["text", "lcov"]
   */
  readonly coverageReporters?: string[];
}

/**
 * Options for the Vitest component.
 */
export interface VitestOptions {
  /**
   * Config file path.
   *
   * @default "vitest.config.ts"
   */
  readonly configFilePath?: string;

  /**
   * Vitest config (test block).
   */
  readonly config?: VitestConfigOptions;

  /**
   * Vitest version (overrides VERSION.VITEST_VERSION).
   *
   * Normally omitted: where the parent monorepo's pnpm catalog carries a
   * `vitest` entry, the rendered specifier is `catalog:` and that entry
   * decides the resolved version. Setting this pins the exact version
   * instead, bypassing the catalog — prefer bumping the catalog entry.
   *
   * @default - the parent monorepo's catalog pin for `vitest`
   */
  readonly vitestVersion?: string;
}

/**
 * Vitest component for Node/TypeScript projects.
 *
 * Adds Vitest as the test runner: devDeps, vitest.config.ts, and test/watch tasks.
 * Incompatible with Jest; use jest: false when constructing the project.
 */
export class Vitest extends Component {
  /**
   * Find the Vitest component on a project.
   */
  public static of(project: NodeProject): Vitest | undefined {
    const isVitest = (c: Component): c is Vitest => c instanceof Vitest;
    return project.components.find(isVitest);
  }

  private readonly configFilePath: string;
  private readonly include: string[];
  private readonly exclude: string[];
  private readonly environment: string;
  private readonly passWithNoTests: boolean;
  private readonly coverageEnabled: boolean;
  private readonly coverageDirectory: string;
  private readonly coverageReporters: string[];
  private readonly coverageInclude: string[];

  constructor(
    public readonly project: NodeProject,
    options: VitestOptions = {},
  ) {
    super(project);

    this.configFilePath = options.configFilePath ?? "vitest.config.ts";
    const config = options.config ?? {};
    this.include = config.include ?? ["**/*.{test,spec}.?(c|m)[jt]s?(x)"];
    this.exclude = config.exclude ?? [
      "**/node_modules/**",
      "**/dist/**",
      "**/lib/**",
      "**/.?*",
    ];
    this.environment = config.environment ?? "node";
    this.passWithNoTests = config.passWithNoTests ?? true;
    this.coverageEnabled = config.coverageEnabled ?? true;
    this.coverageDirectory = config.coverageDirectory ?? "coverage";
    this.coverageReporters = config.coverageReporters ?? ["text", "lcov"];
    this.coverageInclude = config.coverageInclude ?? [
      "src/**/*.{js,cjs,mjs,jsx,ts,cts,mts,tsx}",
    ];

    /**
     * Resolve the pin from the parent monorepo's pnpm catalog when it carries
     * a `vitest` entry, so the catalog stays the single place the version
     * moves. An explicit `vitestVersion` still wins — unlike `AwsCdkProject`,
     * this component treats the option as a supported escape hatch rather
     * than a conflict to reject, because consumers use it to step off the
     * default pin without forking the catalog.
     *
     * With no catalog behind it, `catalog:` resolves to nothing and pnpm
     * fails on a protocol error, so fall back to rendering the version
     * literally.
     */
    const catalogVersion = PnpmWorkspace.of(project)?.defaultCatalog?.vitest;
    const specifier =
      options.vitestVersion === undefined && catalogVersion !== undefined
        ? "catalog:"
        : (options.vitestVersion ?? VERSION.VITEST_VERSION);

    project.addDevDeps(`vitest@${specifier}`);
    if (this.coverageEnabled) {
      /**
       * `@vitest/coverage-v8` declares an exact `vitest` peer dependency, so
       * it tracks the same specifier rather than a range of its own.
       */
      project.addDevDeps(`@vitest/coverage-v8@${specifier}`);
    }

    const coveragePath = `/${this.coverageDirectory}/`;
    project.gitignore.addPatterns(coveragePath);
    project.npmignore?.exclude(coveragePath);

    this.addTestTasks();
    this.synthesizeConfig();
  }

  public override preSynthesize(): void {
    super.preSynthesize();
    for (const component of this.project.components) {
      if (component instanceof Jest) {
        throw new Error("Vitest cannot be used together with Jest");
      }
    }
  }

  private addTestTasks(): void {
    // No receiveArgs so CI/turbo build:all always get a single run (no watch).
    // --update matches Jest's --updateSnapshot: updates snapshots when they mismatch.
    this.project.testTask.exec("vitest run --update");

    if (!this.project.tasks.tryFind("test:watch")) {
      this.project.addTask("test:watch", {
        description: "Run tests in watch mode",
        exec: "vitest watch",
        receiveArgs: true,
      });
    }
  }

  private synthesizeConfig(): void {
    this.project.tryRemoveFile(this.configFilePath);
    new TextFile(this, this.configFilePath, {
      lines: this.renderConfig(),
    });
  }

  private renderConfig(): string[] {
    return [
      'import { defineConfig } from "vitest/config";',
      "",
      "export default defineConfig({",
      "  test: {",
      `    include: ${JSON.stringify(this.include)},`,
      `    exclude: ${JSON.stringify(this.exclude)},`,
      `    environment: "${this.environment}",`,
      `    passWithNoTests: ${this.passWithNoTests},`,
      "    coverage: {",
      `      enabled: ${this.coverageEnabled},`,
      `      provider: "v8",`,
      `      reportsDirectory: "${this.coverageDirectory}",`,
      `      reporter: ${JSON.stringify(this.coverageReporters)},`,
      `      include: ${JSON.stringify(this.coverageInclude)},`,
      "    },",
      "  },",
      "});",
    ];
  }
}
